import { Skeleton, Table, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MaterialIcon from "material-icons-react";
import { addSpaces, cashConverter } from "../../utils";
import svg38 from "../../assets/svg/svg38.svg";
import FilterModal from "./FilterModal";
import { fetchInvoices } from "../../features/invoice/invoiceSlice";
import AccpetanceModal from "./AccpetanceModal";
import "./invoice.css";

function formatInvoiceDate(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function humanizeStatus(status) {
  if (!status) {
    return "Unknown";
  }

  return String(status)
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getStatusTone(status) {
  if (status === "PAID") {
    return "success";
  }

  if (status === "PENDING_PAYMENT") {
    return "warning";
  }

  if (status === "FAILED" || status === "CANCELLED") {
    return "danger";
  }

  return "neutral";
}

function Invoices() {
  const { invoiceData, invoiceCount, loading } = useSelector(
    (state) => state.inv
  );
  const { user } = useSelector((state) => state.auth);
 
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const showModal = () => {
    setIsModalOpen(true);
  };

  const [isModalAcceptance, setIsModalAcceptance] = useState(false);
  const showModalConfirmation = () => {
    setIsModalAcceptance(true);
  };

  const hasApprove = user?.role === "ACCOUNTANT" || user?.role === "SUPER_ADMIN";

  const [prodd, setProdd] = useState();
  const activeFilterCount = Object.values(formData || {}).filter(
    (value) => value !== undefined && value !== null && value !== ""
  ).length;

  const columns = [
    {
      title: "Code",
      dataIndex: "invoCode",
      width: 160,
      ellipsis: true,
      render: (value) => <span className="invoice-code-cell">{value || "-"}</span>,
    },
    {
      title: "Amount",
      dataIndex: "invoAmount",
      width: 160,
      align: "right",
      render: (value) => <span className="invoice-money-cell">{cashConverter(value || 0)}</span>,
    },
    {
      title: "Pay Mobile",
      dataIndex: "invoPayerMobileNumber",
      width: 150,
      render: (value) => <span className="invoice-mobile-cell">{addSpaces(value) || "-"}</span>,
    },
    {
      title: "Created By Email",
      dataIndex: "invoCreatedByEmail",
      width: 240,
      ellipsis: true,
      render: (value) => (
        <Tooltip title={value}>
          <span className="invoice-email-cell">{value || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "invoCreatedDate",
      width: 210,
      render: (value) => <span className="invoice-date-cell">{formatInvoiceDate(value)}</span>,
    },
    {
      title: "Due Date",
      dataIndex: "invoDueDate",
      width: 210,
      render: (value) => <span className="invoice-date-cell">{formatInvoiceDate(value)}</span>,
    },
    {
      title: "Status",
      dataIndex: "invoStatus",
      width: 180,
      render: (_, item) => {
        const status = item?.invoStatus;
        return (
          <span className={`invoice-status-chip invoice-status-chip--${getStatusTone(status)}`}>
            {humanizeStatus(status)}
          </span>
        );
      },
    },
    ...(hasApprove
      ? [
          {
            title: "Actions",
            width: 140,
            align: "center",
            render: (item) => (
              <button
                type="button"
                disabled={item?.invoStatus === "PAID"}
                className={`invoice-action-button ${
                  item?.invoStatus === "PAID" ? "is-disabled" : ""
                }`}
                onClick={() => {
                  setProdd(item);
                  showModalConfirmation();
                }}
              >
                {item?.invoStatus === "PAID" ? "Approved" : "Approve"}
              </button>
            ),
          },
        ]
      : []),
  ];

  const handleClearFilters = async () => {
    setPageIndex(0);
    await setFormData({});
    await dispatch(
      fetchInvoices({
        url: "api/v2/credit/invoice-list",
        limit: pageSize,
        start: 0,
      })
    );
  };

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  async function fetchInvoiceData(page, size) {
    await dispatch(
      fetchInvoices({
        url: "api/v2/credit/invoice-list",
        limit: size ?? pageSize,
        start: page ?? pageIndex,
        invoStatus: formData?.invoStatus,
        invoDate: formData?.invoDate,
        invoPayerMobileNumber: formData?.invoPayerMobileNumber,
      })
    );
  }

  useEffect(() => {
    fetchInvoiceData();
  }, []);

  return (
    <>
      <div className="invoice-page w-full h-full overflow-y-auto weiser-soft-scrollbar">
        <div className="invoice-shell">
          <section className="invoice-toolbar-card">
            <div className="invoice-toolbar-copy">
              <h2>Invoices</h2>
              <p>Review requested invoices, payment status, and approval activity.</p>
            </div>

            <div className="invoice-toolbar-actions">
              <button
                onClick={showModal}
                type="button"
                className={`invoice-filter-button ${activeFilterCount > 0 ? "is-active" : ""}`}
              >
                <MaterialIcon color="currentColor" icon="filter_list" />
                <span>Filters</span>
                {activeFilterCount > 0 && <strong>{activeFilterCount}</strong>}
              </button>

              {activeFilterCount > 0 && (
                <button type="button" className="invoice-clear-button" onClick={handleClearFilters}>
                  <img src={svg38} alt="clear filters" />
                  <span>Clear filters</span>
                </button>
              )}
            </div>
          </section>

          <section className="invoice-table-card">
            <div className="invoice-table-head">
              <div>
                <h3>Requested invoices</h3>
                <p>{invoiceCount || invoiceData?.length || 0} records available</p>
              </div>
            </div>

            {loading && (!invoiceData || invoiceData.length === 0) ? (
              <Skeleton />
            ) : (
              <div className="invoice-table-shell">
                <Table
                  className="invoice-table"
                  scroll={{
                    x: 1290,
                  }}
                  tableLayout="fixed"
                  pagination={{
                    position: ["bottomCenter"],
                    current: pageIndex + 1,
                    total: invoiceCount,
                    pageSize: pageSize,
                    onChange: (page, size) => {
                      setPageIndex(page - 1);
                      setPageSize(size);
                      fetchInvoiceData(page - 1, size);
                    },
                    showSizeChanger: false,
                    hideOnSinglePage: true,
                  }}
                  locale={{
                    emptyText: (
                      <div className="invoice-empty-state">
                        <span>No invoices found for the current filters.</span>
                      </div>
                    ),
                  }}
                  rowKey={(record) => record?.invoId}
                  columns={columns}
                  dataSource={invoiceData}
                  loading={loading}
                  rowClassName={() => "invoice-table-row"}
                />
              </div>
            )}
          </section>
        </div>
      </div>

      <FilterModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        formData={formData}
        setFormData={setFormData}
      />

      <AccpetanceModal
        isModalOpen={isModalAcceptance}
        setIsModalOpen={setIsModalAcceptance}
        prodd={prodd}
        title={`Approve ${prodd?.invoCode}`}
      />
    </>
  );
}

export default Invoices;
 
